import * as React from 'react';
import { Search, Building2, Hash, FileText } from 'lucide-react';

import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';

export interface ClienteOption {
  id: number;
  legal_name: string;
  company_tax_id?: string;
  trade_name?: string;
}

interface ClientSelectModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientes: ClienteOption[];
  value: number | null;
  onSelect: (id: number) => void;
  placeholder?: string;
}

export function ClientSelectModal({
  open,
  onOpenChange,
  clientes,
  value,
  onSelect,
  placeholder = 'Selecione o cliente',
}: ClientSelectModalProps) {
  const [search, setSearch] = React.useState('');
  const filtered = React.useMemo(() => {
    if (!search.trim()) return clientes;
    const q = search.trim().toLowerCase();
    return clientes.filter((c) => {
      const name = (c.legal_name || '').toLowerCase();
      const trade = (c.trade_name || '').toLowerCase();
      const cnpj = (c.company_tax_id || '').replace(/\D/g, '');
      const searchDigits = q.replace(/\D/g, '');
      const idStr = String(c.id);
      return (
        name.includes(q) ||
        trade.includes(q) ||
        (searchDigits && cnpj.includes(searchDigits)) ||
        idStr.includes(q)
      );
    });
  }, [clientes, search]);

  const handleSelect = (id: number) => {
    onSelect(id);
    onOpenChange(false);
    setSearch('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-2xl max-w-[95vw]">
          <DialogHeader>
            <DialogTitle>Selecionar cliente</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar por nome, CNPJ ou ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                autoFocus
              />
            </div>
            <ScrollArea className="h-[320px] rounded-md border">
              <div className="p-2 space-y-1">
                {filtered.length === 0 ? (
                  <div className="py-8 text-center text-sm text-muted-foreground">
                    Nenhum cliente encontrado.
                  </div>
                ) : (
                  filtered.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => handleSelect(c.id)}
                      className={cn(
                        'w-full flex items-center gap-3 rounded-lg px-3 py-3 text-left transition-colors',
                        value === c.id
                          ? 'bg-primary text-primary-foreground'
                          : 'hover:bg-muted'
                      )}
                    >
                      <div className={cn(
                        'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg',
                        value === c.id ? 'bg-white/20' : 'bg-primary/10'
                      )}>
                        <Building2 className={cn(
                          'h-5 w-5',
                          value === c.id ? 'text-primary-foreground' : 'text-primary'
                        )} />
                      </div>
                      <div className="min-w-0 flex-1 overflow-hidden">
                        <p className="font-medium break-words">{c.legal_name}</p>
                        {c.company_tax_id && (
                          <p className={cn(
                            'text-xs flex items-center gap-1 mt-0.5',
                            value === c.id ? 'text-primary-foreground/80' : 'text-muted-foreground'
                          )}>
                            <FileText className="h-3 w-3 shrink-0" />
                            {c.company_tax_id}
                          </p>
                        )}
                      </div>
                      <span className={cn(
                        'text-xs shrink-0 flex items-center gap-1',
                        value === c.id ? 'text-primary-foreground/80' : 'text-muted-foreground'
                      )}>
                        <Hash className="h-3 w-3" />
                        {c.id}
                      </span>
                    </button>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>
  );
}
